import React from "react";

import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { createDateAsUTC, isFullDay } from "@default/lib/date-time-helper";
import { defaultLocale } from "@default/js/app";

/**
 * All options for the date and time formats.
 * Comment out the options you don't need. Then they won't be included in the output.
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat#syntax
 */
const FORMAT_OPTIONS = {
  short: {
    minute: "2-digit",
    hour: "2-digit",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  },
  medium: {
    weekday: "short",
    minute: "2-digit",
    hour: "2-digit",
    day: "numeric",
    month: "short",
    year: "numeric",
  },
  long: {
    minute: "2-digit",
    hour: "2-digit",
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  },
  weekday_short: {
    weekday: "short",
  },
  weekday_long: {
    weekday: "long",
  },
  short_no_year: {
    minute: "2-digit",
    hour: "2-digit",
    day: "2-digit",
    month: "2-digit",
  },
  event_date: {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "long",
    hour: "2-digit",
    minute: "2-digit",
  },
  event_date_time: {
    weekday: "short",
    day: "numeric",
    month: "2-digit",
    year: "numeric",
    minute: "2-digit",
    hour: "2-digit",
  },
  event_time: {
    minute: "2-digit",
    hour: "2-digit",
  },
};

/**
 * DateTime Component to format date and time values.
 *
 * @param {Object} props - Component properties.
 * @param {string|number} props.value - The start date/time of the range.
 * @param {string|number} [props.endValue=null] - The end date/time of the range. If not provided, only the start date is shown.
 * @param {boolean} [props.showEnd=true] - Whether to show the end date/time or not.
 * @param {string} [props.mode="short"] - The mode of the date format. Can be "short", "medium", or "long".
 * @param {string} [props.granularity="datetime"] - The granularity of the date format. Can be "time", "date", or "datetime".
 * @returns
 */
const DateTime = ({
  value,
  endValue = null,
  showEnd = true,
  mode = "short",
  granularity = "datetime",
  relative = false,
}) => {
  let language = useSelector((state) => state.i18n.currentLanguage);
  if (!language || language === "und") {
    language = defaultLocale;
  }

  let startDate,
    endDate,
    formatOptions = {};

  if (!showEnd || !endValue) {
    startDate = createDateAsUTC(new Date(value), typeof value);
  } else {
    startDate = createDateAsUTC(new Date(value), typeof value);
    endDate = createDateAsUTC(new Date(endValue), typeof value);
  }

  if (isNaN(startDate)) {
    return <FormattedMessage id="error.invalid_date" />;
  }

  // Don't show time, if granularity is set to date only
  if (granularity === "date") {
    formatOptions = {
      day: FORMAT_OPTIONS[mode].day,
      month: FORMAT_OPTIONS[mode].month,
      year: FORMAT_OPTIONS[mode].year,
      weekday: FORMAT_OPTIONS[mode].weekday,
    };
  }

  if (granularity === "archived") {
    formatOptions = {
      day: "numeric",
      month: "numeric",
      year: "numeric",
    };
  }

  // Don't show date, if granularity is set to time only
  if (granularity === "time") {
    formatOptions = {
      hour: FORMAT_OPTIONS[mode].hour,
      minute: FORMAT_OPTIONS[mode].minute,
    };
  }

  // show everything, if granularity is set to datetime
  if (granularity === "datetime") {
    formatOptions = {
      ...FORMAT_OPTIONS[mode],
    };
  }

  // Don't show time, if it's a full day event
  if (isFullDay(startDate, endDate)) {
    formatOptions.hour = undefined;
    formatOptions.minute = undefined;
  }

  // Intl.DateTimeFormat falls back to a year/month/day default when given no
  // options at all, so a time-only granularity suppressed by isFullDay above
  // must render nothing rather than an unwanted date.
  if (Object.values(formatOptions).every((option) => option === undefined)) {
    return null;
  }

  const dateTimeFormat = new Intl.DateTimeFormat(language, {
    ...formatOptions,
    timeZone: "Europe/Berlin",
  });

  // Relative time formatting
  if (relative && !endDate) {
    const relativeDateTimeFormat = new Intl.RelativeTimeFormat(language, {
      numeric: "auto",
      style: "long",
    });

    const now = new Date();
    const diffInMs = startDate - now;
    const diffInHours = Math.round(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24));
    const diffInMonths = Math.round(diffInMs / (1000 * 60 * 60 * 24 * 30));
    const diffInYears = Math.round(diffInMs / (1000 * 60 * 60 * 24 * 365));

    if (Math.abs(diffInHours) < 24) {
      return <time>{relativeDateTimeFormat.format(diffInHours, "hour")}</time>;
    }

    if (Math.abs(diffInDays) < 30) {
      return <time>{relativeDateTimeFormat.format(diffInDays, "day")}</time>;
    }

    if (Math.abs(diffInMonths) < 12) {
      return (
        <time>{relativeDateTimeFormat.format(diffInMonths, "month")}</time>
      );
    }

    if (Math.abs(diffInYears) >= 1) {
      return <time>{relativeDateTimeFormat.format(diffInYears, "year")}</time>;
    }
  }

  if (!showEnd || !endDate) {
    return <time>{dateTimeFormat.format(startDate)}</time>;
  }

  // formatRange's ICU pattern silently adds day/month/year to disambiguate a
  // range that crosses midnight, even though granularity="time" explicitly
  // asked for hour/minute only. Format each side independently to keep a
  // time-only granularity strictly time-only.
  if (granularity === "time") {
    if (startDate.getTime() === endDate.getTime()) {
      return <time>{dateTimeFormat.format(startDate)}</time>;
    }

    return (
      <time>
        {dateTimeFormat.format(startDate)}–{dateTimeFormat.format(endDate)}
      </time>
    );
  }

  return <time>{dateTimeFormat.formatRange(startDate, endDate)}</time>;
};

export default DateTime;
